"use client";

import { useState, FormEvent } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import Image from "next/image";

// Dynamically import the ModelViewer with no SSR to avoid hydration issues
const ModelViewer = dynamic(() => import("@/Components/ModelViewer"), {
  ssr: false,
});

export default function LoginPage(): JSX.Element {
  const [empid, setEmpid] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const router = useRouter();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ empid, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      // Redirect to dashboard on successful login
      router.push("/dashboard");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Logo and Company Name */}
      <div className="bg-white w-full md:w-1/2 flex flex-col items-center justify-center p-4">
        {/* Container for 3D model with fixed dimensions */}
        <div
          className="w-full h-[50vh] mb-4 flex justify-center items-center "
          style={{ position: "relative" }}
        >
          {/* <ModelViewer /> */}
          <Image
            src="/assets/Logo_new.png"
            alt="Logo"
            // fill
            // style={{ objectFit: "contain" }}
            height={400}
            width={300}
          />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 text-center mt-4">
          Accura Tequipment
        </h1>
      </div>

      {/* Right Side - Login Form */}
      <div className="bg-[#0790e8] w-full md:w-1/2 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow-md w-full max-w-md p-8">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
            Login
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="empid"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Employee ID
              </label>
              <input
                id="empid"
                type="text"
                value={empid}
                onChange={(e) => setEmpid(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0790e8] text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
